import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AdminAuthProvider, useAdminAuth } from "./contexts/AdminAuthContext";
import ErrorBoundary from "./components/ErrorBoundary";

// User Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import WaitingApproval from "./pages/WaitingApproval";
import Blocked from "./pages/Blocked";
import Payment from "./pages/Payment";
import Dashboard from "./pages/Dashboard";
import TrialExpired from "./pages/TrialExpired";
import SharedIdea from "./pages/SharedIdea";

// Admin Pages
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminSettings from "./pages/admin/AdminSettings";

// Protected Route for Users
const ProtectedRoute = ({ children }) => {
  const { user, loading, token } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  if (user?.status === 'pending') {
    return <Navigate to="/aguardando" replace />;
  }
  
  if (user?.status === 'blocked') {
    return <Navigate to="/bloqueado" replace />;
  }
  
  if (user?.status === 'expired') {
    return <Navigate to="/trial-expirado" replace />;
  }
  
  return children;
};

// Protected Route for Admin
const AdminProtectedRoute = ({ children }) => {
  const { token, loading } = useAdminAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

// Public Route - Redirect if authenticated
const PublicRoute = ({ children }) => {
  const { user, token, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (token && (user?.status === 'active' || user?.status === 'trial')) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/cadastro" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />
      
      {/* Status Routes */}
      <Route path="/aguardando" element={<WaitingApproval />} />
      <Route path="/bloqueado" element={<Blocked />} />
      <Route path="/pagamento" element={<Payment />} />
      <Route path="/trial-expirado" element={<TrialExpired />} />
      <Route path="/esqueci-senha" element={<ForgotPassword />} />
      
      {/* Protected User Routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={
        <AdminProtectedRoute>
          <AdminDashboard />
        </AdminProtectedRoute>
      } />
      <Route path="/admin/pagamentos" element={
        <AdminProtectedRoute>
          <AdminPayments />
        </AdminProtectedRoute>
      } />
      <Route path="/admin/configuracoes" element={
        <AdminProtectedRoute>
          <AdminSettings />
        </AdminProtectedRoute>
      } />
      
      {/* Public Shared Idea Route */}
      <Route path="/compartilhado/:shareId" element={<SharedIdea />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <AdminAuthProvider>
              <AppRoutes />
            </AdminAuthProvider>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
